import { Component, booleanAttribute, inject, input, signal, computed, model } from '@angular/core';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { ClipboardService } from 'ngx-clipboard';

import { JsonSafePipe, EllipsisPipe } from '@pipes';
import { HttpApiError } from '@models/api/http-api-error';
import { copyHtmlToClipboard } from '@models/utils/dom-ui';
import { BaseMin } from '@utils/base-min';

@Component({
    selector: 'vz-error-panel',
    templateUrl: 'error-panel.component.html',
    styleUrls: ['./error-panel.component.less'],
    imports: [NzButtonModule, EllipsisPipe, JsonSafePipe]
})

export class ErrorPanelComponent extends BaseMin {
    readonly error              = model<HttpApiError>();
    readonly border             = input<boolean, unknown>(true, { transform: booleanAttribute });
    readonly etype              = input<string>();
    readonly showClearButton    = input<boolean, unknown>(false, { transform: booleanAttribute });

    readonly showMore       = signal<boolean>(false);
    readonly copied         = signal<boolean>(false);
    readonly realType       = computed<string | undefined>(() => {
        const err = this.error();
        let et = this.etype();
        if (!et) {
            if (err?.type) {
                et = err.type;
            }
            else if (err && (err.status == 200 || err.status == 403 || err.status == 404 || err.status == 409)) {
                et = 'info';
            }
        }
        return et;
    });

    private readonly _cb        = inject(ClipboardService);

    copyError(): void {
        if (!this.error()) {
            return;
        }
        this.copied.set(true);
        const [html, text] = this.getErrorReportText();
        copyHtmlToClipboard(html, text).subscribe(res => {
            if (!res) {
                this._cb.copyFromContent(text);
            }
            setTimeout(() => this.copied.set(false), 5000);
        });
    }

    getErrorReportText(): [string, string] {
        const error = this.error();
        if (!error) {
            return ['', ''];
        }
        const js = new JsonSafePipe();
        const html = '<html><body><!--StartFragment--><h3 data-pm-slice="1 1 []"><strong>ОШИБКА</strong></h3>'
            + '<p><strong>' + error.method +' </strong><a href="'
            + error.url + '" target="_blank" rel="noopener noreferrer nofollow" style="margin-left: 4px; margin-right: 4px; cursor: text;">'
            + '<code>' + error.url + '</code></a></p><p></p>'
            + '<p><strong><u>Запрос:</u></strong>' + (error.body ? '' : ' <i>&lt;Пусто&gt;</i>') + '</p>'
            + (error.body ? '<div class="vz-codeblock-wrapper"><pre><code class="language-json">' + js.transform(error.body, 4) + '</code></pre></div>' : '')
            + '<p></p><p><strong><u>Ответ:</u></strong>' + (error.error ? '' : ' <i>&lt;Пусто&gt;</i>') + '</p>'
            + (error.error ? '<div class="vz-codeblock-wrapper"><pre><code class="language-json">' + js.transform(error.error, 4) + '</code></pre></div>' : '')
            + '<!--EndFragment--></body></html>';
        const text =
            '**ОШИБКА**: ' + error.title + ' '.repeat(30) + '\n'
            + error.text + '\n---\n'
            + '**' + error.method + '** ' + error.url + '\n\n'
            + '**Request**: ' + (error.body ? '\n```json\n' + js.transform(error.body, 4) + '\n```' : 'Empty') + '\n\n'
            + '**Response**: ' + (error.error ? '\n```json\n' + js.transform(error.error, 4) + '\n```' : 'Empty') + '\n\n';
        return [html, text];
    }

    getErrorReportDoc(): any {
        const js = new JsonSafePipe();
        const error = this.error();
        return error
            ? {
                type: 'doc',
                content: [
                    {
                        type: 'heading',
                        attrs: { level: 3 },
                        content: [{ type: 'text', text: 'ОШИБКА: ' + error.title }]
                    },
                    {
                        type: 'paragraph',
                        content: [
                            {
                                type: 'text',
                                marks: [{ type: 'bold' }],
                                text: error.method + ' '
                            },
                            {
                                type: 'text',
                                marks: [{ type: 'link', attrs: { href: error.url } }],
                                text: error.url
                            },
                        ]
                    },
                    {
                        type: 'paragraph',
                        content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Request:' }]
                    },
                    {
                        type: 'codeBlock',
                        attrs: { language: 'json' },
                        content: [{ type: 'text', text: error.body ? js.transform(error.body, 4) : '<Empty>' }]
                    },
                    {
                        type: 'paragraph',
                        content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Response:' }]
                    },
                    {
                        type: 'codeBlock',
                        attrs: { language: 'json' },
                        content: [{ type: 'text', text: error.error ? js.transform(error.error, 4) : '<Empty>' }]
                    }
                ]
            }
            : undefined;
    }

}
